import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import useDarkMode from "../../hooks/useDarkMode";
import useThemeColor from "../../hooks/useThemeColor";
import { Link, useNavigate } from "react-router-dom";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import swal from "sweetalert";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import {
  setVisibleColumns,
  setSortOrder,
  setSortBy,
  setFilterField,
} from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import ModernGridToolbar from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState";
import TableActionsMenu, {
  TableActionsItem,
} from "../../components/CustomComponents/TableActionsMenu/TableActionsMenu";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader";
import useResetPageOnFilterChange from "../../hooks/useResetPageOnFilterChange";
import useDebouncedEffect from "../../hooks/useDebouncedEffect";
import useInvalidateFetchOnFilterChange from "../../hooks/useInvalidateFetchOnFilterChange";
import {
  applyCatalogTotalFromFetch,
  applyCatalogTotalFromMutation,
} from "../../utils/catalogTotalRecords";


import "./manageRole.scss";

const ROLE_SORT_FIELDS = {
  name: "Role_Name",
  order: "Role_Orderby",
};

const ROLE_SORT_LABELS = {
  [ROLE_SORT_FIELDS.name]: "Name",
  [ROLE_SORT_FIELDS.order]: "Order By",
};

const ManageRole = () => {
  const authUser = useSelector((state) => state.auth.user);
  const CompID = authUser?.userData?.compID;
  const loggedInUserRoles = authUser?.userData?.userRoles;
  const canViewPermanentRoles =
    loggedInUserRoles?.isAdmin === true || loggedInUserRoles?.isAdmin === 1;
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [catalogTotalRecords, setCatalogTotalRecords] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const rolesFetchSeqRef = useRef(0);

  const pageKey = "roles";
  const defaultVisibleColumns = {
    name: true,
    backOffice: true,
    allowRefunds: true,
    allowPettyCash: true,
    actions: true,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Role_Orderby", label: "Order By" };
  const defaultPageSize = { value: "50", label: "50" };
  const validPageSizes = ["20", "50", "100"];

  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const pageSize =
    preferences.pageSize &&
    validPageSizes.includes(String(preferences.pageSize.value))
      ? preferences.pageSize
      : defaultPageSize;
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();

  const handlePageSizeChange = useCallback(
    (value) => {
      dispatch(setFilterField({ pageKey, field: "pageSize", value }));
    },
    [dispatch, pageKey]
  );

  const clearFilters = () => {
    setSearch("");
  };

  const handleColumnSort = useCallback(
    (sortField) => {
      const nextOrder =
        sortBy?.value === sortField && sortOrder?.value === "DESC"
          ? { value: "ASC", label: "ASC" }
          : { value: "DESC", label: "DESC" };

      dispatch(
        setSortBy({
          pageKey,
          sortBy: {
            value: sortField,
            label: ROLE_SORT_LABELS[sortField] ?? sortField,
          },
        })
      );
      dispatch(setSortOrder({ pageKey, sortOrder: nextOrder }));
    },
    [dispatch, pageKey, sortBy?.value, sortOrder?.value]
  );

  // API returns full list; pagination is client-side on top-level rows only.
  const fetchRoleData = useCallback(async () => {
    const fetchSeq = ++rolesFetchSeqRef.current;
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      if (fetchSeq !== rolesFetchSeqRef.current) return;

      const roleData = response.data.data || response.data;
      const list = Array.isArray(roleData) ? roleData : [];
      setRoles(list);

      if (!search.trim()) {
        applyCatalogTotalFromFetch(setCatalogTotalRecords, list.length);
      }
    } catch (err) {
      if (fetchSeq !== rolesFetchSeqRef.current) return;
      setRoles([]);
      showErrorToast("Failed to fetch roles.");
    } finally {
      if (fetchSeq === rolesFetchSeqRef.current) {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    }
  }, [search, sortBy, sortOrder, CompID]);

  const totalRecords = roles.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / Number(pageSize.value)));
  const pagedRoles = useMemo(() => {
    const size = Number(pageSize.value);
    const start = (pageNumber - 1) * size;
    return roles.slice(start, start + size);
  }, [roles, pageNumber, pageSize]);

  useEffect(() => {
    if (!CompID || CompID <= 0) {
      showErrorToast("Invalid Company ID. Please log in again.");
      navigate("/user/login");
    }
  }, [CompID, navigate]);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
      const mergedColumns = mergeVisibleColumns(
        preferences.visibleColumns,
        defaultVisibleColumns
      );
      dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    if (
      !preferences.pageSize ||
      !validPageSizes.includes(String(preferences.pageSize?.value))
    ) {
      dispatch(setFilterField({ pageKey, field: "pageSize", value: defaultPageSize }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, pageKey]);

  useResetPageOnFilterChange(setPageNumber, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useInvalidateFetchOnFilterChange(rolesFetchSeqRef, [
    search,
    sortBy?.value,
    sortOrder?.value,
    pageSize?.value,
  ]);

  useDebouncedEffect(() => fetchRoleData(), [fetchRoleData]);

  useEffect(() => {
    if (!initialLoadComplete) return;
    setLoading(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, sortBy?.value, sortOrder?.value]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const hasFiltersApplied = () => search.trim() !== "";

  const showEmptyCatalogState =
    initialLoadComplete &&
    !loading &&
    !hasFiltersApplied() &&
    catalogTotalRecords === 0;

  const showGridToolbar = initialLoadComplete && !showEmptyCatalogState;

  const getEmptyStateDescription = () => {
    if (search.trim()) {
      return `No roles match your search "${search.trim()}". Try different keywords or clear filters.`;
    }
    return "There are no roles to show right now. Add a new role or check back later.";
  };

  const renderNoDataMessage = () => {
    if (loading || roles.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No roles found"
        description={getEmptyStateDescription()}
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  const handleAddRole = () => navigate("/role/AddRole");

  const handleDeleteRole = async (roleItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete role ${roleItem.r_Title}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (!roleItem.comRolePermission_ID || !CompID || CompID <= 0) {
        showErrorToast("Invalid role or company ID.");
        return;
      }

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_ROLE, {
        params: {
          CompID,
          id: roleItem.comRolePermission_ID,
        },
      });

      if (response.data.status) {
        setRoles((prevRoles) => {
          const next = prevRoles.filter(
            (item) => item.comRolePermission_ID !== roleItem.comRolePermission_ID
          );
          if (!search.trim()) {
            applyCatalogTotalFromMutation(setCatalogTotalRecords, next.length);
          }
          return next;
        });
        showSuccessToast("Role deleted successfully.");
      } else {
        showErrorToast(response.data.message || "Deletion failed.");
      }
      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong while deleting the role.");
      setLoading(false);
    }
  };

  const isPermanentRole = (role) =>
    role.isPermanentRole === true || role.isPermanentRole === 1;

  const canViewRole = (role) => !isPermanentRole(role) || canViewPermanentRoles;

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Role</li>
          </ol>
          <h1 className="page-header mb-0">Manage Role</h1>
        </div>
        <div className="ms-auto">
          <button
            type="button"
            className="btn px-4"
            onClick={handleAddRole}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
          >
            <i className="fa fa-plus fa-lg me-2 ms-n2 text-white" />
            Add Role
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        {!initialLoadComplete && <GridSectionLoader loading minHeight="400px" />}

        {showEmptyCatalogState && (
          <AddButton onClick={handleAddRole} title="Add Role" />
        )}

        {showGridToolbar && (
          <ModernGridToolbar
            isDarkMode={isDarkMode}
            title="Roles"
            totalCount={totalRecords}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by role name..."
            bodyFlush
            headerControls={
              <ColumnSelection
                visibleColumns={visibleColumns}
                setVisibleColumns={(cols) =>
                  dispatch(setVisibleColumns({ pageKey, columns: cols }))
                }
              />
            }
          >
            <div className="px-3 pb-3">
              <GridSectionLoader loading={loading} minHeight="280px">
                {renderNoDataMessage()}

                {!loading && pagedRoles.length > 0 && (
                  <div
                    className="table-responsive table-container"
                    style={{
                      minHeight: "200px",
                      maxHeight: "450px",
                      overflowY: "auto",
                      overflowX: "auto",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <table
                      className="table table-hover manage-role-table"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead
                        className="bg-light sticky-header"
                        style={{
                          position: "sticky",
                          top: 0,
                          zIndex: 10000,
                          backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                          color: isDarkMode ? "#ffffff" : "#000000",
                        }}
                      >
                        <tr>
                          <th className="pt-2 pb-2" />
                          {visibleColumns.name && (
                            <SortableColumnHeader
                              sortField={ROLE_SORT_FIELDS.name}
                              sortBy={sortBy}
                              sortOrder={sortOrder}
                              onSort={handleColumnSort}
                            >
                              Role Name
                            </SortableColumnHeader>
                          )}
                          {visibleColumns.backOffice && (
                            <th className="pt-2 pb-2 text-center">Back Office</th>
                          )}
                          {visibleColumns.allowRefunds && (
                            <th className="pt-2 pb-2 text-center">Allow Refunds</th>
                          )}
                          {visibleColumns.allowPettyCash && (
                            <th className="pt-2 pb-2 text-center">Allow Petty Cashes</th>
                          )}
                          {visibleColumns.actions && (
                            <th className="pt-2 pb-2 text-center">Actions</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {pagedRoles.map((role) => (
                          <tr key={role.comRolePermission_ID}>
                            <td className="align-middle" />
                            {visibleColumns.name && (
                              <td
                                className="align-middle"
                                style={{
                                  cursor: canViewRole(role) ? "pointer" : "default",
                                }}
                                onClick={() => {
                                  if (canViewRole(role)) {
                                    navigate(
                                      `/role/updateRole/${role.comRolePermission_ID}`
                                    );
                                  }
                                }}
                              >
                                {role.r_Title}
                              </td>
                            )}
                            {visibleColumns.backOffice && (
                              <td className="align-middle text-center">
                                <StatusBadge
                                  isActive={role.isBackOffice}
                                  activeLabel="Yes"
                                  inactiveLabel="No"
                                  disabled
                                />
                              </td>
                            )}
                            {visibleColumns.allowRefunds && (
                              <td className="align-middle text-center">
                                <StatusBadge
                                  isActive={role.allowRefunds}
                                  activeLabel="Yes"
                                  inactiveLabel="No"
                                  disabled
                                />
                              </td>
                            )}
                            {visibleColumns.allowPettyCash && (
                              <td className="align-middle text-center">
                                <StatusBadge
                                  isActive={role.allowPettyCashes}
                                  activeLabel="Yes"
                                  inactiveLabel="No"
                                  disabled
                                />
                              </td>
                            )}
                            {visibleColumns.actions && (
                              <td className="align-middle text-center">
                                <TableActionsMenu dropup menuStyle={{ zIndex: 9999 }}>
                                  {(!isPermanentRole(role) || canViewPermanentRoles) && (
                                    <TableActionsItem
                                      as={Link}
                                      to={`/role/updateRole/${role.comRolePermission_ID}`}
                                      icon="fa fa-edit"
                                    >
                                      Edit
                                    </TableActionsItem>
                                  )}
                                  {!isPermanentRole(role) && (
                                    <TableActionsItem
                                      danger
                                      icon="fa fa-trash"
                                      onClick={() => handleDeleteRole(role)}
                                    >
                                      Delete
                                    </TableActionsItem>
                                  )}
                                  {isPermanentRole(role) && !canViewPermanentRoles && (
                                    <div className="dropdown-item text-muted text-center">
                                      Permanent Role - No Actions
                                    </div>
                                  )}
                                </TableActionsMenu>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <ModernGridPagerFooter
                  pageSize={pageSize}
                  onPageSizeChange={handlePageSizeChange}
                  totalRecords={totalRecords}
                  pageNumber={pageNumber}
                  totalPages={totalPages}
                  setPageNumber={setPageNumber}
                />
              </GridSectionLoader>
            </div>
          </ModernGridToolbar>
        )}
      </div>
    </div>
  );
};

export default ManageRole;
